import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Box, TextField, Button, DialogTitle, DialogContent, DialogActions, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import ScaleIcon from '@mui/icons-material/Scale';

const UnitForm = ({ selected, onSuccess, onCancel }) => {
  const [name, setName] = useState('');
  const [shortCode, setShortCode] = useState('');
  const [parentId, setParentId] = useState('');
  const [conversion, setConversion] = useState('1');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selected) {
      setName(selected.name);
      setShortCode(selected.short_code);
      setParentId(selected.parent_id || '');
      setConversion(selected.conversion || '1');
    } else {
      setName('');
      setShortCode('');
      setParentId('');
      setConversion('1');
    }
  }, [selected]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = { 
        name, 
        short_code: shortCode,
        parent_id: parentId || null,
        conversion: parseFloat(conversion) || 1
      };
      
      if (selected) {
        await axios.put(`/api/units/${selected.id}`, data);
      } else {
        await axios.post('/api/units', data);
      }
      onSuccess();
      setName('');
      setShortCode('');
      setParentId('');
      setConversion('1');
    } catch (error) {
      console.error('Error saving unit:', error);
      alert('Failed to save unit');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1, 
        bgcolor: '#00acc1',
        color: 'white',
        fontSize: '1rem',
        py: 1.5
      }}>
        <ScaleIcon />
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
          {selected ? 'Edit Unit' : 'Add New Unit'}
        </Typography>
      </DialogTitle>
      
      <DialogContent dividers>
        <Box component="form" id="unit-form" onSubmit={handleSubmit} sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <TextField
            fullWidth
            value={name}
            onChange={e => setName(e.target.value)}
            label="Unit Name"
            placeholder="e.g., Kilogram"
            required
            autoFocus
            sx={{ 
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: '#4caf50',
                }
              }
            }}
          />

          <TextField
            fullWidth
            value={shortCode}
            onChange={e => setShortCode(e.target.value)}
            label="Short Code"
            placeholder="e.g., kg"
            required
            sx={{ 
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: '#4caf50',
                }
              }
            }}
          />

          <TextField
            fullWidth
            value={parentId}
            onChange={e => setParentId(e.target.value)}
            label="Parent ID (optional)"
            placeholder="e.g., 1"
            type="number"
            sx={{ 
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: '#4caf50',
                }
              }
            }}
          />

          <TextField
            fullWidth
            value={conversion}
            onChange={e => setConversion(e.target.value)}
            label="Conversion"
            placeholder="e.g., 1"
            type="number"
            inputProps={{ step: '0.01' }}
            sx={{ 
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: '#4caf50',
                }
              }
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ py: 1, px: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'grey.50', gap: 1 }}>
        <Button 
          onClick={onCancel}
          variant="outlined"
          startIcon={<CancelIcon />}
          sx={{ 
            borderColor: '#757575',
            color: '#757575',
            '&:hover': {
              borderColor: '#424242',
              bgcolor: '#f5f5f5'
            }
          }}
        >
          Cancel
        </Button>
        <Button 
          type="submit"
          form="unit-form"
          variant="contained" 
          disabled={loading}
          startIcon={selected ? <EditIcon /> : <AddIcon />}
          sx={{ 
            bgcolor: '#00acc1',
            fontSize: '0.75rem',
            py: 0.5,
            px: 1.5,
            '&:hover': {
              bgcolor: '#00838f'
            }
          }}
        >
          {selected ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </>
  );
};

export default UnitForm;
